"use client";

import { c as _c } from "react/compiler-runtime";
import { useId } from "react";
import { Button } from "@repo/ui/button";
import { LinkButton } from "@repo/ui/link-button";
import { SolidShape } from "@repo/ui/solid-shape";
import { cn } from "@repo/ui/utils";
import { ActionLink } from "@/components/common/actions/actions.component";
import { EditorialGlyph } from "@/components/common/editorial-glyph/editorial-glyph.component";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { getI18nString } from "@/lib/utils";
import type { VisualFeatureFullWidthProps } from "./visual-feature-full-width.types";

/**
 * Full-width visual feature block.
 *
 * Layout: a full-bleed image/video background (aspect-ratio based) with a
 * 50% dark overlay, centered content (decorative shape, headline, body, CTA)
 * rendered on top in white text.
 */
export function VisualFeatureFullWidth(t0) {
  const $ = _c(30);
  const {
    headline,
    body,
    actions,
    ctaStyle: t1,
    mark: t2,
    visual,
    className
  } = t0;
  const ctaStyle = t1 === undefined ? "link" : t1;
  const mark = t2 === undefined ? "shape" : t2;
  const headlineId = useId();
  let t3;
  if ($[0] !== className) {
    t3 = cn("scroll-motion-scope dark relative w-full overflow-hidden", "aspect-[390/560] desktop:aspect-[1440/832]", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== visual) {
    t4 = visual && <VisualWrapper visual={visual} className="visual-drift absolute inset-0 h-full w-full" visualClassName="h-full w-full" imgClassName="h-full w-full object-cover" />;
    $[2] = visual;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div aria-hidden="true" className="absolute inset-0 bg-black/50" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== mark) {
    t6 = mark === "subSite" ? <EditorialGlyph className="size-12 desktop-sm:size-16" /> : <div className="grid size-12 place-items-center bg-surface-highlight-light-green desktop:size-16"><SolidShape variant="diamond" color="turquoise" animated={false} className="size-[68%]" /></div>;
    $[5] = mark;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== headline || $[8] !== headlineId) {
    t7 = headline && <h2 id={headlineId} className="w-full typography-mobile-heading-h3 desktop:typography-desktop-heading-h4">{headline}</h2>;
    $[7] = headline;
    $[8] = headlineId;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== body) {
    t8 = body && <RichTextCondensed richText={body} as="body-medium" />;
    $[10] = body;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <div className="flex w-full flex-col items-center gap-4 text-center text-text-primary desktop:gap-6">{t7}{t8}</div>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== actions || $[16] !== ctaStyle || $[17] !== headline || $[18] !== headlineId) {
    t10 = actions && actions.length > 0 && <div className="flex flex-wrap items-center gap-4">{actions.map(action => {
        if (!action?.to) {
          return null;
        }
        return <ActionLink key={action._id} actionTo={action.to} aria-labelledby={headline ? headlineId : undefined}>{ctaStyle === "button" ? <Button asChild={true} variant="white-filled" size="lg"><span>{getI18nString(action.text, "Learn more")}</span></Button> : <LinkButton asChild={true} underline={true} variant="primary" size="md"><span>{getI18nString(action.text, "Learn more")}</span></LinkButton>}</ActionLink>;
      })}</div>;
    $[15] = actions;
    $[16] = ctaStyle;
    $[17] = headline;
    $[18] = headlineId;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <div className="flex w-full max-w-[644px] flex-col items-center gap-6 desktop:gap-12">{t9}{t10}</div>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t6) {
    t12 = <div className="container-grid absolute inset-0 h-full items-center"><div className="col-span-full grid h-full grid-cols-subgrid items-center gap-4 tablet:gap-6"><div className="col-span-full flex flex-col items-center gap-8 tablet:col-span-6 tablet:col-start-2 desktop:col-span-6 desktop:col-start-4 desktop:gap-10">{t6}{t11}</div></div></div>;
    $[23] = t11;
    $[24] = t6;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t3 || $[28] !== t4) {
    t13 = <section className={t3} data-testid="visual-feature-full-width">{t4}{t5}{t12}</section>;
    $[26] = t12;
    $[27] = t3;
    $[28] = t4;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}